import {
  Quicksand_500Medium,
  Quicksand_600SemiBold,
  Quicksand_700Bold,
  useFonts,
} from '@expo-google-fonts/quicksand';
import { router, Stack, usePathname } from 'expo-router';
import * as SplashScreen from 'expo-splash-screen';
import { StatusBar } from 'expo-status-bar';
import { useEffect } from 'react';
import { AppState, Platform } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';

import { trackScreen } from '@/analytics/analytics';
import { ShareToast } from '@/components/share-toast';
import '@/i18n';
import { attachPushTapRouting } from '@/notifications/push';
import { useAppMetaStore } from '@/store/app-meta-store';
import { useAppHydration } from '@/store/use-app-hydration';
import { colors } from '@/theme/tokens';
import { recordForecastSnapshotIfNeeded } from '@/utils/forecast-snapshot';

SplashScreen.preventAutoHideAsync();

export default function RootLayout() {
  const [fontsLoaded] = useFonts({
    Quicksand_500Medium,
    Quicksand_600SemiBold,
    Quicksand_700Bold,
  });
  const dbReady = useAppHydration();
  const ready = fontsLoaded && dbReady;
  const pathname = usePathname();
  const metaHydrated = useAppMetaStore((s) => s.hydrated);
  const onboardingDone = useAppMetaStore((s) => s.onboardingDone);

  useEffect(() => {
    if (ready) SplashScreen.hideAsync();
  }, [ready]);

  // Owner: overscroll "rubber-band" was revealing a white strip behind the app
  // (the browser paints the <body> bg). Paint html/body the app background so a
  // fixed, non-moving backdrop shows through in both scroll directions.
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;
    document.documentElement.style.backgroundColor = colors.background;
    document.body.style.backgroundColor = colors.background;
  }, []);

  // First-run gate: a brand-new install lands on onboarding before anything
  // else. Once completed (flag persisted), we never send them here again.
  useEffect(() => {
    if (!ready || !metaHydrated) return;
    if (!onboardingDone && pathname !== '/onboarding') {
      router.replace('/onboarding');
    }
  }, [ready, metaHydrated, onboardingDone, pathname]);

  // Single, central screen-view instrumentation: the route path is the screen
  // name (no PII in our routes). No-op until the user opts into analytics.
  useEffect(() => {
    if (ready && pathname) {
      trackScreen(pathname);
      // Count screen opens to time the one-off "discover the ideas board" nudge.
      void useAppMetaStore.getState().bumpScreenView();
    }
  }, [ready, pathname]);

  // A phone left open across midnight would miss the daily forecast snapshot
  // (hydration runs once per cold start) — foregrounding re-checks the KV
  // day-stamp, so this is one cheap read on most activations, one write a day.
  useEffect(() => {
    if (!ready) return undefined;
    const sub = AppState.addEventListener('change', (state) => {
      if (state === 'active') void recordForecastSnapshotIfNeeded().catch(() => undefined);
    });
    return () => sub.remove();
  }, [ready]);

  // Server-push taps route into the app (e.g. chat push -> the conversation).
  // Attached once; handles the cold-start tap too. Web: no-op.
  useEffect(() => {
    if (!ready) return undefined;
    let unsubscribe: (() => void) | null = null;
    void attachPushTapRouting((path) => router.push(path as never)).then((unsub) => {
      unsubscribe = unsub;
    });
    return () => {
      unsubscribe?.();
    };
  }, [ready]);

  if (!ready) return null;

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <StatusBar style="dark" />
      <Stack
        screenOptions={{
          headerShown: false,
          contentStyle: { backgroundColor: colors.background },
        }}
      />
      {/* Screenshot -> share nudge (native only, once a day, never on vault) */}
      <ShareToast />
    </GestureHandlerRootView>
  );
}
